<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { NButton, useMessage } from 'naive-ui'
import ReviewQueueShell from './ReviewQueueShell.vue'
import LintNoteCard from './LintNoteCard.vue'
import LintNoteEditModal from './LintNoteEditModal.vue'
import { useLintNotesStore, type LintNote } from '../stores/lint_notes'
import { useReviewQueueRun } from '../composables/useReviewQueueRun'

// Admin-only review queue for pending content findings. The queue
// scaffold (RouteShell + load/error/auth/empty/data branches + summary +
// onMounted/needsAuth wiring) lives in ReviewQueueShell; this view
// supplies the lint store, the LintNoteCard row, and the inline edit modal.
// Auth gating happens at the router level — non-admins are bounced to /.

const store = useLintNotesStore()
const message = useMessage()

// "Run lint now" trigger — POSTs an ad-hoc LintJob and locks the
// button until the run finishes. New findings stream into the queue via
// the store's channel subscription as the run lands them.
const run = useReviewQueueRun('lint_runs')
onMounted(() => void run.refresh())

async function runNow(): Promise<void> {
  try {
    await run.trigger()
    message.info('Lint run queued — new findings will appear here as they land.')
  } catch (e) {
    message.error(e instanceof Error ? e.message : 'Failed to queue lint run')
  }
}

const editing = ref<LintNote | null>(null)
const editOpen = ref(false)

// Kind buckets for the summary line, most actionable first.
const LINT_KIND_LABELS: Record<LintNote['kind'], string> = {
  contradiction: 'contradiction',
  stale_claim: 'stale claim',
  unreinforced_claim: 'unreinforced claim',
}

function onEdit(note: LintNote) {
  editing.value = note
  editOpen.value = true
}
</script>

<template>
  <ReviewQueueShell
    :store="store"
    :items="store.notes"
    document-title="Lint review"
    heading="Lint review queue"
    :kind-labels="LINT_KIND_LABELS"
    empty-description="No open lint notes — every finding has been reviewed."
    list-testid="lint-list"
  >
    <template #actions>
      <NButton
        size="small"
        :loading="run.triggering.value"
        :disabled="run.running.value || run.triggering.value"
        data-testid="lint-run-now"
        @click="runNow"
        >{{ run.running.value ? 'Lint running…' : 'Run lint now' }}</NButton
      >
    </template>

    <template #description>
      Open content findings that need your call. Accept applies the proposed patch. Dismiss closes the row without
      changing the page. Edit refines the patch with a free-form prompt.
    </template>

    <template #item="{ item }">
      <LintNoteCard :note="item" @edit="onEdit" />
    </template>

    <template #after>
      <LintNoteEditModal v-model:show="editOpen" :note="editing" />
    </template>
  </ReviewQueueShell>
</template>
